/**
 * F9 session time limits: when this tab last had input from the person (a key, a click, a scroll, the mouse moving).
 * Every request to the server says how long ago that was (X-MatchPoint-Idle, whole seconds), so background polling
 * doesn't keep an unattended sign-in alive: the server's idle clock follows the person, not the requests
 * (backend app/auth/session_limits.py). A request sent straight after a click reports 0.
 */
import type { InternalAxiosRequestConfig, AxiosInstance } from 'axios';

export const IDLE_HEADER = 'X-MatchPoint-Idle';
export const SIGNED_OUT_REASON_KEY = 'mpSignedOutReason';

const INPUT_EVENTS = ['keydown', 'pointerdown', 'wheel', 'touchstart', 'mousemove', 'scroll'] as const;
const THROTTLE_MS = 1000;

let lastInput = Date.now();

/** Something the person did just now. */
export const noteInput = (at: number = Date.now()) => {
  if (at > lastInput) lastInput = at;
};

export const lastInputAt = () => lastInput;

/** Whole seconds since this tab last had input. */
export const idleSeconds = (now: number = Date.now()) => Math.max(0, Math.floor((now - lastInput) / 1000));

let tracking = false;

/** Listen for input on the whole page (once). */
export const startActivityTracking = () => {
  if (tracking || typeof window === 'undefined') return;
  tracking = true;
  let last = 0;
  const onInput = () => {
    const now = Date.now();
    if (now - last < THROTTLE_MS) return;
    last = now;
    noteInput(now);
  };
  INPUT_EVENTS.forEach(e => window.addEventListener(e, onInput, { passive: true, capture: true }));
};

/** The request interceptor: say how long this tab has been idle. */
export const addIdleHeader = (config: InternalAxiosRequestConfig) => {
  config.headers?.set?.(IDLE_HEADER, String(idleSeconds()));
  return config;
};

export const withIdleHeader = (instance: AxiosInstance) => {
  instance.interceptors.request.use(addIdleHeader);
  return instance;
};

/** For tests. */
export const resetActivity = (at: number = Date.now()) => {
  lastInput = at;
};
